/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid,jsx-a11y/role-supports-aria-props */
import { Snackbar } from "@material-ui/core";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { shallowEqual, useSelector } from "react-redux";

import axios from "axios";
import { Field, Form, Formik } from "formik";
import { ButtonGroup, Button, Spinner } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import * as Yup from "yup";
import { yupNumber } from "../../helpers/yupNumber";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
  Input,
} from "../../../_metronic/_partials/controls";
import { useSubheader } from "../../../_metronic/layout";
import TextEditor from "../../../_zebra/_partials/TextEditor";
import { SnackbarWrapper } from "../Snackbar/SnackbarWrapper";
import {
  attendeePaymentStatus,
  isFreeEvent,
} from "./Components/attendeesExport";

const initEmail = {
  fromEmail: "",
  toEmail: [],
  subject: "",
  emailBody: "",
  bodyLength: 0,
};

// How many failed addresses to name before summarising the rest.
const MAX_FAILED_SHOWN = 5;

// The server's own message for a failed send, plus which addresses failed
// (api/email-service/send answers 502 with { message, errors }).
const sendErrorMessage = (e) => {
  const { message, errors } = e?.response?.data || {};
  if (!message) return "Something went wrong";
  if (!errors?.length) return message;
  const shown = errors.slice(0, MAX_FAILED_SHOWN).join(", ");
  const more = errors.length - MAX_FAILED_SHOWN;
  return `${message}: ${shown}${more > 0 ? ` and ${more} more` : ""}`;
};

const EmailSchema = Yup.object().shape({
  toEmail: Yup.array()
    .of(
      Yup.object()
        .shape({ value: Yup.string(), label: Yup.string() })
        .required("At least one email is required")
    )
    .min(1, "Need at least one email"),
  subject: Yup.string()
    .min(5, "subject must be at least 5 character")
    .required("Subject is required"),
  bodyLength: yupNumber()
    .min(5, "Body is required, minimum of 5 characters")
    .required("Email body is required"),
});

const FILTERS = ["All", "Paid", "Unpaid"];

/**
 * One recipient option per parent email (lowercased), so a parent with two
 * children at the event gets one email. Payment follows attendeePaymentStatus,
 * as on the Event List and the export: a family is unpaid if any of its
 * students is "Not paid", and otherwise paid: families who owe nothing
 * ($0 participant fee, "Free") are listed under Paid too. Rows without a
 * parent email are counted, not listed.
 */
const toRecipients = (results, event) => {
  const byEmail = new Map();
  let missing = 0;
  [...(results?.confirmed || []), ...(results?.pending || [])].forEach(
    (attendee) => {
      const email = (attendee.parent_email || "").trim();
      if (!email) {
        missing += 1;
        return;
      }
      const key = email.toLowerCase();
      const status = attendeePaymentStatus(event, attendee);
      const existing = byEmail.get(key);
      if (existing) {
        existing.students.push(attendee.student_name);
        existing.statuses.add(status);
      } else {
        byEmail.set(key, {
          email,
          parentName: attendee.parent_name,
          students: [attendee.student_name],
          statuses: new Set([status]),
        });
      }
    }
  );
  const options = [...byEmail.values()].map((r) => ({
    value: r.email,
    label: `${r.parentName ? `${r.parentName} ` : ""}<${r.email}> (${r.students.join(", ")})`,
    unpaid: r.statuses.has("Not paid"),
  }));
  return { options, missing };
};

export function EventEmail({
  history,
  location,
  match: {
    params: { id },
  },
}) {
  const branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );
  // Sent explicitly for the same reason as EventRow.js: axios.defaults only
  // gains x-auth-token after the rehydrate saga resolves.
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  const suhbeader = useSubheader();

  const workshopName = location?.state?.workshopName;
  const fee = location?.state?.fee;
  const hasPricedOptions = location?.state?.hasPricedOptions;
  // Only what attendeePaymentStatus and isFreeEvent read from an Event List row.
  const event = useMemo(
    () => ({ fee, has_priced_options: hasPricedOptions }),
    [fee, hasPricedOptions]
  );
  // Without the fee (page opened by URL) the event can't be told free, so the
  // payment filter stays available.
  const free = fee !== undefined && isFreeEvent(event);
  const title = workshopName ? `Email Attendees: ${workshopName}` : "Event Email";

  const [successOpen, setSuccessOpen] = useState(false);
  // The error snackbar text; empty while it is closed.
  const [errorMessage, setErrorMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [branchEmail, setBranchEmail] = useState("");
  const [contacts, setContacts] = useState([]);
  const [missingCount, setMissingCount] = useState(0);
  const [filter, setFilter] = useState("All");
  // status: "loading" | "error" | "loaded"
  const [contactsStatus, setContactsStatus] = useState({ status: "loading", forbidden: false });
  const [retryCount, setRetryCount] = useState(0);
  const [plainTextBody, setPlainTextBody] = useState("");

  const btnRef = useRef();
  // Loaded recipients go in through Formik so a retry keeps the typed draft
  // (reinitializing the form would reset subject and body too).
  const formikRef = useRef();

  const retry = useCallback(() => setRetryCount((n) => n + 1), []);

  useEffect(() => {
    suhbeader.setTitle(title);
  }, [title]);

  useEffect(() => {
    if (!branch) return;
    axios(`api/email-service/get-email?branchId=${branch.branch_id}`)
      .then((result) => setBranchEmail(result.data.results[0]?.email || ""))
      .catch((e) => console.error(e));
  }, [branch]);

  useEffect(() => {
    if (!authToken) return undefined;
    let cancelled = false;
    setContactsStatus({ status: "loading", forbidden: false });
    axios(`api/workshops/attendee-details/${id}`, {
      headers: { "x-auth-token": authToken },
    })
      .then((result) => {
        if (cancelled) return;
        const { options, missing } = toRecipients(result.data.results, event);
        setContacts(options);
        setMissingCount(missing);
        setFilter("All");
        // No validation yet: errors show untouched here, so the empty subject
        // and body would flag before staff had typed anything.
        formikRef.current?.setFieldValue("toEmail", options, false);
        setContactsStatus({ status: "loaded", forbidden: false });
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setContactsStatus({ status: "error", forbidden: e.response?.status === 403 });
      });
    return () => {
      cancelled = true;
    };
  }, [id, authToken, retryCount, event]);

  const sendEmail = async (values) => {
    if (sending) return;
    setSending(true);
    const sendUrl = "api/email-service/send";
    const payload = {
      fromEmail: branchEmail,
      branchId: branch.branch_id,
      subject: values.subject,
      emailBody: values.emailBody,
      // Each parent gets their own email; the branch is BCC'd once.
      to: [],
      bcc: values.toEmail.map((e) => e.value),
      bccBranchOnce: true,
    };

    try {
      const result = await axios.post(sendUrl, payload);
      if (result.data !== "failed") {
        setSuccessOpen(true);
        setTimeout(() => {
          history.push(`/events/event-list`);
        }, 1000);
        return;
      }
      setErrorMessage("Something went wrong");
    } catch (e) {
      console.error(e);
      setErrorMessage(sendErrorMessage(e));
    }
    setSending(false);
  };

  const handleClose = () => {
    setSuccessOpen(false);
    setErrorMessage("");
  };

  const sendEmailClick = () => {
    if (btnRef && btnRef.current) {
      btnRef.current.click();
    }
  };

  const backToEventList = () => {
    history.push(`/events/event-list`);
  };

  const createableOnChange = (setFieldValue, currentValues, name) => {
    let splitableOptions = currentValues
      ? currentValues.filter((v) => {
          return v.value.indexOf(",") >= 0;
        })
      : [];
    let normalOptions = currentValues
      ? currentValues.filter((v) => {
          return v.value.indexOf(",") < 0;
        })
      : [];
    let finalOptions = [...normalOptions];
    splitableOptions.map((v) => {
      v.value.split(",").map((val) => {
        const trimmed = val.trim();
        trimmed && finalOptions.push({ value: trimmed, label: trimmed });
      });
    });
    setFieldValue(name, finalOptions);
  };

  // Resets the To list to the chosen subset; staff can still edit it after.
  const applyFilter = (setFieldValue, nextFilter) => {
    setFilter(nextFilter);
    setFieldValue(
      "toEmail",
      contacts.filter((c) =>
        nextFilter === "Paid" ? !c.unpaid : nextFilter === "Unpaid" ? c.unpaid : true
      )
    );
  };

  return (
    <Fragment>
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={successOpen || Boolean(errorMessage)}
        // Long enough to read which addresses failed.
        autoHideDuration={successOpen ? 3000 : 10000}
        onClose={handleClose}
      >
        <SnackbarWrapper
          onClose={handleClose}
          variant={successOpen ? "success" : "error"}
          message={
            successOpen ? "Sent the email successfully" : errorMessage
          }
        />
      </Snackbar>
      <Card>
        <CardHeader title={title}>
          <CardHeaderToolbar>
            <button
              type="button"
              onClick={backToEventList}
              className="btn btn-light"
            >
              <i className="fa fa-arrow-left"></i>
              Back
            </button>
            {`  `}
            <button
              type="submit"
              className="btn btn-primary ml-2"
              onClick={sendEmailClick}
              disabled={sending || !branchEmail || contactsStatus.status !== "loaded"}
            >
              {sending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Send"
              )}
            </button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <Formik
            innerRef={formikRef}
            initialValues={initEmail}
            validationSchema={EmailSchema}
            onSubmit={(values) => {
              sendEmail(values);
            }}
          >
            {({
              handleSubmit,
              values,
              errors,
              setFieldValue,
              setFieldTouched,
            }) => (
              <>
                <Form className="form form-label-right">
                  <div className="form-group row">
                    <div className="col-lg-6">
                      <label className={"required-field"}>From</label>
                      <Field
                        name="fromEmail"
                        component={Input}
                        placeholder="Your email"
                        type="text"
                        value={branchEmail}
                        disabled={true}
                      />
                      <small className="form-text text-muted">
                        A copy will be BCC'd to this address.
                      </small>
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-lg-6">
                      <label className={"required-field"}>To</label>
                      {contactsStatus.status === "loading" ? (
                        <div className="d-flex align-items-center text-muted">
                          <Spinner animation="border" size="sm" className="mr-2" />
                          Loading attendees…
                        </div>
                      ) : contactsStatus.status === "error" ? (
                        <p className="mb-0 text-danger">
                          {contactsStatus.forbidden ? (
                            "You don't have access to this event's attendees."
                          ) : (
                            <>
                              Couldn't load attendees.{" "}
                              <button
                                type="button"
                                className="btn btn-link p-0 align-baseline"
                                onClick={retry}
                              >
                                Try again
                              </button>
                            </>
                          )}
                        </p>
                      ) : (
                        <>
                          {!free && contacts.length > 0 && (
                            <ButtonGroup size="sm" className="mb-2 d-block">
                              {FILTERS.map((f) => (
                                <Button
                                  key={f}
                                  variant={filter === f ? "primary" : "light"}
                                  onClick={() => applyFilter(setFieldValue, f)}
                                >
                                  {f}
                                </Button>
                              ))}
                            </ButtonGroup>
                          )}
                          <Field
                            component={CreatableSelect}
                            isMulti
                            name="toEmail"
                            options={contacts}
                            value={values.toEmail}
                            className={`${errors.toEmail ? "is-invalid" : ""}`}
                            onChange={(selectedOptions) => {
                              createableOnChange(
                                setFieldValue,
                                selectedOptions,
                                "toEmail"
                              );
                            }}
                            onBlur={() => {
                              createableOnChange(
                                setFieldValue,
                                values.toEmail,
                                "toEmail"
                              );
                            }}
                            placeholder="Select or Create email recipients"
                          />
                          {errors.toEmail && (
                            <div className="invalid-feedback">{errors.toEmail}</div>
                          )}
                          <small className="form-text text-muted">
                            {contacts.length === 0
                              ? "No attendees with an email yet."
                              : `${values.toEmail.length} recipient(s); each gets their own email.`}
                            {missingCount > 0 &&
                              ` ${missingCount} attendee(s) have no parent email on file.`}
                          </small>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-lg-6">
                      <label className={"required-field"}>Subject</label>
                      <Field
                        name="subject"
                        component={Input}
                        placeholder="Subject"
                        type="text"
                        onChange={(e) => {
                          setFieldValue("subject", e.target.value);
                        }}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-lg-12">
                      <label className={"required-field"}>Body</label>

                      <TextEditor
                        name="emailBody"
                        value={values["emailBody"]}
                        setValue={setFieldValue}
                        setPlainTextBody={setPlainTextBody}
                        onBlur={(e) => {
                          setFieldTouched("emailBody", true);
                          setFieldTouched("bodyLength", true);
                        }}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-lg-12">
                      {errors.bodyLength ? (
                        <p className="mt-10">
                          <small className="text-danger">
                            {errors.bodyLength}
                          </small>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="form-group row">
                    <button
                      type="submit"
                      style={{ display: "none" }}
                      ref={btnRef}
                      onSubmit={() => handleSubmit()}
                    ></button>
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </CardBody>
      </Card>
    </Fragment>
  );
}
